import * as React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { EventType, MuteRow } from './index';

interface Props {
    mutes: MuteRow[];
    // Kept in the props for future use (e.g. event-type-level mutes); not
    // referenced by describe() since v1 mutes are always entity-scoped.
    eventTypes: EventType[];
    onUnmute: (muteId: number) => void | Promise<void>;
}

/**
 * Lists active per-source mutes. New mutes are created from the row kebab on
 * the archive page (e.g. "Mute this project"). Settings page is where you
 * REVIEW them — see the full list, unmute individual entries.
 *
 * Display preference, most specific first:
 *   1. source_label — human name denormalized at mute-creation time ("Enis Kameri")
 *   2. fallback: "{ShortClass} #{id}" — last 2 chars after the namespace ("Project #42")
 */
const MutedSourcesSection: React.FC<Props> = ({ mutes, onUnmute }) => {
    const intl = useIntl();

    const describe = (m: MuteRow): React.ReactNode => {
        if (m.source_label && m.source_label.trim().length > 0) {
            return m.source_label;
        }
        if (m.entity_type && m.entity_id) {
            const short = m.entity_type.split('\\').pop() || m.entity_type;
            return `${short} #${m.entity_id}`;
        }
        return '—';
    };

    return (
        <section className="notif-settings__section notif-settings__section--mutes">
            <header className="notif-settings__section-head">
                <span className="notif-settings__section-icon" aria-hidden="true">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                        <path d="M18.63 13A17.89 17.89 0 0 1 18 8" />
                        <path d="M6.26 6.26A5.86 5.86 0 0 0 6 8c0 7-3 9-3 9h14" />
                        <path d="M18 8a6 6 0 0 0-9.33-5" />
                        <line x1="1" y1="1" x2="23" y2="23" />
                    </svg>
                </span>
                <div className="notif-settings__section-head-text">
                    <h2 className="notif-settings__section-title">
                        <FormattedMessage id="notifications.settings.mutes.title" defaultMessage="Muted sources" />
                    </h2>
                    <p className="notif-settings__section-desc">
                        <FormattedMessage
                            id="notifications.settings.mutes.desc"
                            defaultMessage="Notifications from these sources are suppressed. Add new mutes via the ⋮ menu on any notification row."
                        />
                    </p>
                </div>
                <span className={`notif-settings__pill ${mutes.length > 0 ? 'notif-settings__pill--mutes-active' : 'notif-settings__pill--off'}`}>
                    <FormattedMessage
                        id="notifications.settings.mutes.pillCount"
                        defaultMessage="{count, plural, =0 {0 BURIME} one {# BURIM} other {# BURIME}}"
                        values={{ count: mutes.length }}
                    />
                </span>
            </header>

            {mutes.length === 0 && (
                <div className="notif-settings__empty">
                    <FormattedMessage
                        id="notifications.settings.mutes.empty"
                        defaultMessage="You haven't muted any sources yet."
                    />
                </div>
            )}

            {mutes.length > 0 && (
                <ul className="notif-settings__list">
                    {mutes.map((m) => (
                        <li key={m.id} className="notif-settings__list-row">
                            <div className="notif-settings__list-main">
                                <span className="notif-settings__list-label">{describe(m)}</span>
                                {/* Show the entity type as a small caption when we have a
                                    human label — gives users context like "Enis Kameri (Client)". */}
                                {m.source_label && m.entity_type && (
                                    <span className="notif-settings__list-meta">
                                        {m.entity_type.split('\\').pop() || m.entity_type}
                                    </span>
                                )}
                                {m.muted_until && (
                                    <span className="notif-settings__list-meta">
                                        <FormattedMessage
                                            id="notifications.settings.mutes.expiresAt"
                                            defaultMessage="Expires {when}"
                                            values={{
                                                // Localized via the active intl locale; replaces
                                                // the previous toLocaleString() which fell back
                                                // to the browser's chosen locale (often US-format
                                                // even when the user picked Albanian).
                                                when: intl.formatDate(m.muted_until, {
                                                    day: '2-digit',
                                                    month: 'short',
                                                    year: 'numeric',
                                                    hour: '2-digit',
                                                    minute: '2-digit',
                                                }),
                                            }}
                                        />
                                    </span>
                                )}
                            </div>
                            <button
                                type="button"
                                className="notif-settings__list-action"
                                onClick={() => onUnmute(m.id)}
                                // aria-label always carries the full Albanian/EN
                                // label so screen readers stay accurate when the
                                // visual text is hidden on mobile.
                                aria-label={intl.formatMessage({
                                    id: 'notifications.settings.mutes.unmute',
                                    defaultMessage: 'Unmute',
                                })}
                            >
                                {/* Speaker-off icon — shown ALWAYS; text is
                                    hidden below the mobile breakpoint via the
                                    .notif-settings__list-action-text rule in
                                    the SCSS. Saves ~80px of horizontal width
                                    on narrow viewports. */}
                                <svg
                                    width="16"
                                    height="16"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    className="notif-settings__list-action-icon"
                                    aria-hidden="true"
                                >
                                    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                                    <line x1="23" y1="9" x2="17" y2="15" />
                                    <line x1="17" y1="9" x2="23" y2="15" />
                                </svg>
                                <span className="notif-settings__list-action-text">
                                    <FormattedMessage id="notifications.settings.mutes.unmute" defaultMessage="Unmute" />
                                </span>
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </section>
    );
};

export default MutedSourcesSection;
